/**
 * Wat er voor deze zaak klaarstaat om te vertrekken (plak 2.4).
 *
 * De groene balk na het versturen is een ding van dát moment: hij telt
 * dertig seconden af en verdwijnt zodra je een andere kaart kiest. Bij
 * "morgen 08:00" is dat te weinig — dan staat er een halve dag lang een
 * bericht klaar dat JP moet kunnen zien en terugnemen. Deze strook leest
 * de wachtrij van de zaak en blijft staan zolang er iets klaarstaat.
 *
 * Twee regels van dit scherm:
 *
 * 1. **Uitgeschakeld = uitleggen waarom.** Is het moment voorbij, dan
 *    staat "Terugnemen" uit mét de reden ernaast.
 * 2. **Een mislukte verzending blijft in beeld.** Stil opruimen maakt
 *    onvindbaar; een toast die je gemist hebt is geen melding.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { werkplekTegelApi, type WachtrijRij } from "@/lib/werkplek-api";

interface Props {
  zaakId: string | null;
  /** Meldt de tijdlijn dat er iets veranderd is. */
  onGewijzigd?: () => void;
}

function momentTekst(iso: string): string {
  const moment = new Date(iso);
  const nu = new Date();
  const tijd = moment.toLocaleTimeString("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const zelfdeDag = moment.toDateString() === nu.toDateString();
  if (zelfdeDag) return `vandaag om ${tijd}`;
  const morgen = new Date(nu);
  morgen.setDate(morgen.getDate() + 1);
  if (moment.toDateString() === morgen.toDateString()) {
    return `morgen om ${tijd}`;
  }
  return `${moment.toLocaleDateString("nl-NL", {
    weekday: "long",
    day: "numeric",
    month: "long",
  })} om ${tijd}`;
}

function foutTekst(fout: unknown): string {
  const detail = (
    fout as { response?: { data?: { detail?: unknown } } } | undefined
  )?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  return "Terugnemen lukte niet. Ververs het scherm en kijk opnieuw.";
}

export function Klaarstaand({ zaakId, onGewijzigd }: Props) {
  const queryClient = useQueryClient();

  const rijen = useQuery({
    queryKey: ["werkplek", "wachtrij", zaakId],
    queryFn: () => werkplekTegelApi.wachtrij(zaakId as string),
    enabled: Boolean(zaakId),
    // Het verzendmoment kan verstrijken terwijl het scherm openstaat;
    // dan hoort de knop vanzelf uit te gaan in plaats van te liegen.
    refetchInterval: 15000,
  });

  const intrekken = useMutation({
    mutationFn: (id: string) => werkplekTegelApi.ongedaan(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["werkplek", "wachtrij", zaakId],
      });
      onGewijzigd?.();
    },
  });

  const alles: WachtrijRij[] = rijen.data ?? [];
  const klaar = alles.filter((rij) => rij.status === "wacht");
  const mislukt = alles.filter((rij) => rij.status === "mislukt");
  if (klaar.length === 0 && mislukt.length === 0) return null;

  return (
    <div className="wp-klaarstaand">
      {klaar.map((rij) => (
        <div key={rij.id} className="wp-klaarstaand-rij" role="status">
          <span>
            <strong>Staat klaar</strong>
            {rij.onderwerp ? ` · ${rij.onderwerp}` : ""} · gaat uit{" "}
            {momentTekst(rij.verstuurt_op)}
          </span>
          {rij.mag_intrekken ? (
            <button
              type="button"
              className="wp-knop wp-tegel-knop-klein"
              onClick={() => intrekken.mutate(rij.id)}
              disabled={intrekken.isPending}
            >
              Terugnemen
            </button>
          ) : (
            <span className="wp-klaarstaand-reden">
              Het moment is voorbij; dit bericht is aan de beurt en kan
              niet meer teruggenomen worden.
            </span>
          )}
        </div>
      ))}

      {mislukt.map((rij) => (
        <div
          key={rij.id}
          className="wp-klaarstaand-rij wp-klaarstaand-mislukt"
          role="alert"
        >
          <span>
            <strong>Niet verstuurd</strong>
            {rij.onderwerp ? ` · ${rij.onderwerp}` : ""} ·{" "}
            {rij.foutmelding ?? "de reden is niet vastgelegd"}
          </span>
        </div>
      ))}

      {intrekken.isError ? (
        <p className="wp-tegel-fout">{foutTekst(intrekken.error)}</p>
      ) : null}
    </div>
  );
}
